export class SleeveManager<T extends number[][][] | SleeveDescription[]> {
  private _pathH: any;
  private _pathW: any;
  private _top: number[] = [0, 0];
  private _bottom: number[] = [0, 0];
  private _map: ymaps.Map;
  private _projection: object;
  private _angleLocal: number = 0;
  private _sleeves: Sleeve[] = [];
  private _sleevesMap: SleevesMap = {};

  /**
   * Длина рукава в метрах
   */
  private _sleeveLength: number = 60;

  public get sleeveLength(): number {
    return this._sleeveLength;
  }

  public set sleeveLength(v: number) {
    this._sleeveLength = v;
  }

  /**
   * Ширина рукава в метрах
   */
  private _sleeveWidth: number = 3.5;

  public get sleeveWidth(): number {
    return this._sleeveWidth;
  }

  public set sleeveWidth(v: number) {
    this._sleeveWidth = v;
  }

  /**
   * ctor
   */
  constructor(mapRef: ymaps.Map, options?: Options) {
    this._map = mapRef;
    this._projection = mapRef.options.get('projection', {});
  }

  public dispose() {
    if (this._map) {
      this._map.events.remove('boundschange', this.onBoundsChange);
      this._sleeves = [];
      this._sleevesMap = {};
    }
  }

  /**
   * @param sleeves - массив с географическими координатами точек начала/конца рукавов
   * @example const sleeves = [[[45.05415359546938, 38.97059663223253], [45.05405359546938, 38.97129663223253]]]
   */
  public buildSleevesByStartEndPoints(sleeves: T): Sleeve[] {
    if (sleeves.length === 0) {
      return [];
    }

    this.dispose();

    if (Array.isArray(sleeves[0])) {
      const descriptions = (sleeves as number[][][]).map((position, id) => ({
        id,
        position,
      }));
      this._sleevesMap = this.buildMap(descriptions);
    } else {
      this._sleevesMap = this.buildMap(sleeves as SleeveDescription[]);
    }

    this._sleeves = Object.values(this._sleevesMap);
    this._map.events.add('boundschange', this.onBoundsChange);

    return this._sleeves;
  }

  public setSleeveColor(id: string | number, color: string) {
    const sleeve = this._sleevesMap[id];

    if (!sleeve) {
      return;
    }

    const icon = this.makeSleeveIcon(
      sleeve.widthRel,
      sleeve.heightRel,
      color,
      sleeve.strokeColor,
      sleeve.opacity,
    );

    sleeve.marker.options.set(
      'iconImageHref',
      `data:image/svg+xml,${encodeURIComponent(icon)}`,
    );
  }

  private buildMap(sleeves: SleeveDescription[]) {
    const map: SleevesMap = {};

    if (!sleeves[0].position[0] || !sleeves[0].position[1]) {
      return map;
    }

    this.setPaths(
      sleeves[0].position[0],
      sleeves[0].position[1],
      this._sleeveLength,
      this._sleeveWidth,
    );

    const { width, height } = this.calculateRelativeSize();

    for (const sleeve of sleeves) {
      const [top, bottom] = [sleeve.position[0], sleeve.position[1]];

      if (!top || !bottom) {
        continue;
      }

      const tLocal = this.geoToLocal(top);
      const bLocal = this.geoToLocal(bottom);
      const center = this.getCenter(top, bottom);

      const angleDeg =
        (Math.atan2(bLocal[1] - tLocal[1], bLocal[0] - tLocal[0]) * 180) / Math.PI;

      const shape = this.calculateShape(width, height, this._angleLocal);
      const icon = this.makeSleeveIcon(
        width,
        height,
        sleeve.color,
        sleeve.strokeColor,
        sleeve.opacity,
      );
      const addHintLayout = Array.isArray(sleeve.params) && sleeve.params.length > 0;
      const iconLabelRotate = Math.abs(angleDeg) > 90 ? 90 : -90;
      const iconLabelValue = sleeve.label;

      const marker = new ymaps.Placemark(
        center,
        {},
        {
          // @ts-ignore
          hintLayout: addHintLayout
            ? ymaps.templateLayoutFactory.createClass(`
            <table class="${sleeve.hintClassName}">
              <tbody>
                ${sleeve
                  .params!.map((p) => `<tr><td>${p.label}</td><td>${p.value}</td></tr>`)
                  .join('')}
              </tbody>
            </table>
          `)
            : null,
          iconLayout: ymaps.templateLayoutFactory.createClass(`
            <div style="transform:rotate({{options.rotate}}deg);">
              {% include "default#image" %}
              [if options.labelValue]
                <div style="transform:translate(-50%, -50%) rotate({{ options.labelRotate }}deg);font-size:calc({{ options.imageSize[0] }}px - 1px);position:absolute;top:0;left:0;}">
                  {{ options.labelValue }}
                </div>
              [endif]
            </div>
          `),
          iconImageHref: `data:image/svg+xml,${encodeURIComponent(icon)}`,
          iconImageSize: [width, height],
          iconImageOffset: [-width / 2, -height / 2],
          iconRotate: angleDeg + 90,
          iconShape: sleeve.events
            ? ({
                type: 'Polygon',
                coordinates: [shape],
              } as ymaps.IGeometryJson)
            : null,
          iconLabelRotate,
          iconLabelValue,
        },
      );

      map[sleeve.id] = {
        ...sleeve,
        shape,
        center,
        marker,
        angleDeg,
        widthRel: width,
        heightRel: height,
      };

      if (sleeve.events) {
        const events = sleeve.events;
        marker.events.add('click', (e) => events.click(map[sleeve.id], e));
        marker.events.add('contextmenu', (e) => {
          return events.context(map[sleeve.id], e);
        });
      }
    }
    return map;
  }

  private getCenter(top: number[], bottom: number[]) {
    const maxX = Math.max(top[0], bottom[0]);
    const maxY = Math.max(top[1], bottom[1]);
    const minX = Math.min(top[0], bottom[0]);
    const minY = Math.min(top[1], bottom[1]);

    return [minX + (maxX - minX) / 2, minY + (maxY - minY) / 2];
  }

  private setPaths(top: number[], bottom: number[], length: number, width: number) {
    const angleFwd = Math.atan2(bottom[1] - top[1], bottom[0] - top[0]);
    const angleRgt = angleFwd + Math.PI / 2;

    //const dirFwd = [Math.cos(angleFwd), Math.sin(angleFwd)];
    const dirRgt = [Math.cos(angleRgt), Math.sin(angleRgt)];

    const topLocal = this.geoToLocal(top);
    const botLocal = this.geoToLocal(bottom);

    this._angleLocal =
      Math.atan2(botLocal[1] - topLocal[1], botLocal[0] - topLocal[0]) + Math.PI / 2;

    //const pathH = ymaps.coordSystem.geo.solveDirectProblem(top, dirFwd, length).pathFunction;
    // @ts-ignore
    const pathW = ymaps.coordSystem.geo.solveDirectProblem(
      top,
      dirRgt,
      width,
    ).pathFunction;

    //this._pathH = pathH;
    this._pathW = pathW;

    this._top = top;
    this._bottom = bottom;
  }

  private calculateRelativeSize(zoom?: number) {
    //const p1 = this.geoToLocal(this._pathH(0).point, zoom);
    //const p2 = this.geoToLocal(this._pathH(1).point, zoom);
    const p1 = this.geoToLocal(this._top, zoom);
    const p2 = this.geoToLocal(this._bottom, zoom);
    const p3 = this.geoToLocal(this._pathW(1).point, zoom);

    const width = Math.sqrt(Math.pow(p3[0] - p1[0], 2) + Math.pow(p3[1] - p1[1], 2));
    const height = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));

    return { width, height };
  }

  private geoToLocal(geoCoords: number[], zoom?: number) {
    const zFactor = zoom ?? this._map.getZoom();

    // @ts-ignore
    return this._map.converter.globalToPage(
      this._projection.toGlobalPixels(geoCoords, zFactor),
    );
  }

  private calculateShape(width: number, height: number, angle: number) {
    const dW = width / 2,
      dH = height / 2;
    const [s1, s2, s3, s4] = [
      [-dW, -dH],
      [dW, -dH],
      [dW, dH],
      [-dW, dH],
    ];

    return [
      [
        Math.cos(angle) * s1[0] - Math.sin(angle) * s1[1],
        Math.sin(angle) * s1[0] + Math.cos(angle) * s1[1],
      ],
      [
        Math.cos(angle) * s2[0] - Math.sin(angle) * s2[1],
        Math.sin(angle) * s2[0] + Math.cos(angle) * s2[1],
      ],
      [
        Math.cos(angle) * s3[0] - Math.sin(angle) * s3[1],
        Math.sin(angle) * s3[0] + Math.cos(angle) * s3[1],
      ],
      [
        Math.cos(angle) * s4[0] - Math.sin(angle) * s4[1],
        Math.sin(angle) * s4[0] + Math.cos(angle) * s4[1],
      ],
    ];
  }

  private makeSleeveIcon(
    width: number,
    height: number,
    color = 'white',
    strokeColor = '#000',
    opacity = 0.4,
  ) {
    return `
      <svg preserveAspectRatio="none" width="${width}" height="${height}" viewBox="0 0 17 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_1_4)">
          <path
            opacity="${opacity}"
            d="M11.1355 2.8703L16.5 53.3598L16.5 291.5C16.5 295.918 12.9183 299.5 8.5 299.5C4.08172 299.5 0.5 295.918 0.5 291.5L0.5 53.3598L5.86451 2.8703C6.00771 1.52255 7.14466 0.5 8.5 0.5C9.85534 0.5 10.9923 1.52255 11.1355 2.8703Z"
            fill="${color}"
            stroke="${strokeColor}" />
            <!-- Head -->
<!--          <circle cx="8.73244" cy="295.373" r="10.5641" transform="rotate(90 8.73244 295.373)" stroke="#F1898E" stroke-width="0.918616"/>-->
        </g>
        <defs>
          <clipPath id="clip0_1_4">
            <rect width="300" height="17" fill="${color}" transform="matrix(0 1 -1 0 17 0)" />
          </clipPath>
        </defs>
      </svg>
    `;
  }

  private onBoundsChange = (e: object | ymaps.IEvent) => {
    const event = e as ymaps.Event;
    const newZoom = event.get('newZoom');
    const oldZoom = event.get('oldZoom');

    if (newZoom !== oldZoom) {
      const { width, height } = this.calculateRelativeSize(newZoom);
      const shape = this.calculateShape(width, height, this._angleLocal);

      this._sleeves.forEach((sleeve) => {
        sleeve.shape = shape;
        sleeve.widthRel = width;
        sleeve.heightRel = height;

        if (sleeve.events) {
          sleeve.marker.options.set('iconShape', {
            type: 'Polygon',
            coordinates: [shape],
          });
        }
        sleeve.marker.options.set('iconImageSize', [width, height]);
        sleeve.marker.options.set('iconImageOffset', [-width / 2, -height / 2]);
      });
    }
  };
}

export type SleeveDescription = {
  id: string | number;
  position: number[][];
  color?: string;
  strokeColor?: string;
  opacity?: number;
  events?: Events;
  hintClassName?: string;
  params?: Array<{ label: string; value: string }>;
  label?: string;
};

type Events = {
  click: (target: Sleeve, e: ymaps.Event) => void;
  context: (target: Sleeve, e: ymaps.Event) => void;
};

type Options = {
  test: boolean;
};

export type Sleeve = SleeveDescription & {
  center: number[];
  angleDeg: number;
  widthRel: number;
  heightRel: number;
  shape: number[][];
  marker: ymaps.Placemark;
};

type SleevesMap = { [id: string | number]: Sleeve };
